/**
 * User Authentication Guard
 * Protects user routes with comprehensive security checks
 */
import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import authService from "../services/authService";
import { isTokenExpired, isValidTokenFormat, shouldRedirectToLogin, clearAllAuthData } from "../utils/tokenUtils";

export default function UserAuthGuard({ children }) {
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    const checkAuthentication = () => {
      try {
        const token = localStorage.getItem('authToken');
        
        // Check 1: Token exists
        if (!token) {
          clearAllAuthData();
          setIsAuthorized(false);
          setIsChecking(false);
          return;
        }

        // Check 2: Token format is valid
        if (!isValidTokenFormat(token)) {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('Invalid token format detected');
          }
          clearAllAuthData();
          setIsAuthorized(false);
          setIsChecking(false);
          return;
        }

        // Check 3: Token is not expired
        if (isTokenExpired(token)) {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('Token expired');
          }
          // Try to refresh token before giving up
          const refreshToken = localStorage.getItem('refreshToken');
          if (refreshToken) {
            // Token refresh will be handled by API calls, but we should still redirect
            clearAllAuthData();
          } else {
            clearAllAuthData();
          }
          setIsAuthorized(false);
          setIsChecking(false);
          return;
        }

        // Check 4: User data exists
        const userData = localStorage.getItem('userData');
        if (!userData) {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('User data missing');
          }
          clearAllAuthData();
          setIsAuthorized(false);
          setIsChecking(false);
          return;
        }

        // Check 5: Ensure admin tokens are not present (prevent cross-authentication)
        const adminToken = localStorage.getItem('adminAuthToken');
        if (adminToken) {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('Admin session detected while accessing user routes - clearing admin session');
          }
          localStorage.removeItem('adminAuthToken');
          localStorage.removeItem('adminRefreshToken');
          localStorage.removeItem('adminAuth');
          localStorage.removeItem('adminData');
        }

        // All checks passed
        setIsAuthorized(true);
        setIsChecking(false);
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Authentication check error:', error);
        }
        clearAllAuthData();
        setIsAuthorized(false);
        setIsChecking(false);
      }
    };

    checkAuthentication();

    // Set up periodic token validation (every 5 minutes)
    const validationInterval = setInterval(() => {
      const token = localStorage.getItem('authToken');
      if (token && isTokenExpired(token)) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Token expired during session');
        }
        clearAllAuthData();
        setIsAuthorized(false);
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(validationInterval);
  }, [location.pathname]);

  // Show loading state while checking
  if (isChecking) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column'
      }}>
        <div style={{ fontSize: '18px', marginBottom: '10px' }}>Verifying authentication...</div>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to signin
  if (!isAuthorized) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  // If someone lands on "/user" or "/user/", redirect to dashboard
  if (location.pathname === "/user" || location.pathname === "/user/") {
    return <Navigate to="/user/dashboard" replace />;
  }

  // All security checks passed - grant access
  return children;
}
